/**
 * The one Directory search every picker uses (DirectorySearch,
 * DirectoryNameBox, CareTeamCombobox, ReferralSourceField): debounced, from two characters,
 * and answering only the query as it stands, so nothing from an earlier
 * query can be picked. A result's line under its name says who the entry
 * is: its kind or specialty, its practice and its town.
 */
import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { useTenant } from '@/lib/auth';
import type { SearchResult } from '@/lib/types';

export const DIRECTORY_SEARCH_MIN_CHARS = 2;
const DEBOUNCE_MS = 200;

interface Answer {
  q: string;
  rows: SearchResult[];
}

export interface DirectorySearchState {
  /** The results for the query as it stands; empty until they arrive. */
  rows: SearchResult[];
  /** Waiting for the query as it stands. */
  loading: boolean;
}

export function useDirectorySearch(opts: {
  /** The query, trimmed. */
  q: string;
  /** The box is open (the host's own rule); the query must also be long enough. */
  enabled: boolean;
  limit: number;
  type?: 'organization' | 'individual';
}): DirectorySearchState {
  const { tenantSlug } = useTenant();
  const { q, limit, type } = opts;
  const on = opts.enabled && q.length >= DIRECTORY_SEARCH_MIN_CHARS;
  const [answer, setAnswer] = useState<Answer | null>(null);

  useEffect(() => {
    if (!on) return;
    let cancelled = false;
    const t = setTimeout(() => {
      api
        .searchProviders(tenantSlug, { q, limit, ...(type !== undefined ? { type } : {}) })
        .then((r) => {
          if (!cancelled) setAnswer({ q, rows: r.rows });
        })
        .catch(() => {
          if (!cancelled) setAnswer({ q, rows: [] });
        });
    }, DEBOUNCE_MS);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
  }, [on, q, tenantSlug, limit, type]);

  const answered = on && answer !== null && answer.q === q;
  return { rows: answered ? answer.rows : [], loading: on && !answered };
}

/** The line under a result's name: its kind or specialty, its practice
 *  and its town, the parts present, joined by the house's middle dot. */
export function directoryResultLine(row: SearchResult): string {
  return [row.kind_label ?? row.specialty ?? null, row.practice ?? null, row.town ?? null]
    .filter((v): v is string => v !== null && v.trim() !== '')
    .join(' · ');
}
